import { type ReactNode, createContext, useCallback, useContext, useRef, useState } from 'react'
import { useLatestRef } from './useLatestRef'
import { useAuth } from './useAuth'
import { GuestSignInDialog } from '../components/GuestSignInDialog'
import type { GuestIntent } from '../lib/guestIntent'
import { track } from '../lib/analytics'

export interface GuestPromptContext {
  // What the guest was trying to do. Required so every wall is measurable —
  // guest_prompt_shown without it can't tell an unwanted wall from an unnoticed one.
  intent: GuestIntent
  title?: ReactNode
  description?: ReactNode
  afterSignIn?: { pathname: string; search?: string; hash?: string }
}

interface GuestGuardContextValue {
  isGuest: boolean
  prompt: (ctx: GuestPromptContext) => void
  guard: <Args extends unknown[]>(fn: (...args: Args) => void, ctx: GuestPromptContext) => (...args: Args) => void
}

const GuestGuardContext = createContext<GuestGuardContextValue | null>(null)

export function GuestGuardProvider({ children }: { children: ReactNode }) {
  const { signedInUid, loading } = useAuth()
  const isGuest = !loading && !signedInUid
  const [context, setContext] = useState<GuestPromptContext | null>(null)

  const authRef = useLatestRef({ signedInUid, loading })

  // The accept path closes the dialog on its way to /login, so without this flag
  // every acceptance would also be counted as a dismissal. The intent is held in
  // a ref because the close handler runs after the context has been cleared.
  const openIntentRef = useRef<GuestIntent | null>(null)
  const acceptedRef = useRef(false)

  const openPrompt = useCallback((ctx: GuestPromptContext) => {
    openIntentRef.current = ctx.intent
    acceptedRef.current = false
    setContext(ctx)
    track('guest_prompt_shown', { intent: ctx.intent })
  }, [])

  const prompt = useCallback(
    (ctx: GuestPromptContext) => {
      if (authRef.current.loading || authRef.current.signedInUid) return
      openPrompt(ctx)
    },
    [openPrompt, authRef],
  )

  const guard = useCallback(
    <Args extends unknown[]>(fn: (...args: Args) => void, ctx: GuestPromptContext) =>
      (...args: Args) => {
        if (authRef.current.loading || authRef.current.signedInUid) {
          fn(...args)
          return
        }
        openPrompt(ctx)
      },
    [openPrompt, authRef],
  )

  const handleAccept = useCallback(() => {
    acceptedRef.current = true
    if (openIntentRef.current) track('guest_prompt_accepted', { intent: openIntentRef.current })
  }, [])

  const setDialogOpen = useCallback((open: boolean) => {
    if (open) return
    if (!acceptedRef.current && openIntentRef.current) {
      track('guest_prompt_dismissed', { intent: openIntentRef.current })
    }
    openIntentRef.current = null
    setContext(null)
  }, [])

  return (
    <GuestGuardContext.Provider value={{ isGuest, prompt, guard }}>
      {children}
      <GuestSignInDialog
        open={context !== null}
        onOpenChange={setDialogOpen}
        onAccept={handleAccept}
        title={context?.title}
        description={context?.description}
        afterSignIn={context?.afterSignIn}
      />
    </GuestGuardContext.Provider>
  )
}

// Wraps actions that should prompt anonymous/unauth users to sign up instead
// of running. `guard(fn, ctx)` returns a wrapped fn with the same signature;
// calling it either runs `fn` or opens a contextual sign-up dialog. `prompt`
// triggers the dialog directly for non-callback entry points like a button
// that opens a flow.
// eslint-disable-next-line react-refresh/only-export-components -- hook export co-located with its GuestGuardProvider component
export function useGuestGuard() {
  const ctx = useContext(GuestGuardContext)
  if (!ctx) throw new Error('useGuestGuard must be used within GuestGuardProvider')
  return ctx
}
